import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Camera, Upload, Check, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const PFLICHT_SLOTS = [
  { key: 'front_left', title: 'Vorne links', minCount: 1 },
  { key: 'front_right', title: 'Vorne rechts', minCount: 1 },
  { key: 'rear_left', title: 'Hinten links', minCount: 1 },
  { key: 'rear_right', title: 'Hinten rechts', minCount: 1 },
  { key: 'odometer', title: 'Kilometerstand', minCount: 1 },
  { key: 'damage_wide', title: 'Schadenbereich weit', minCount: 1 },
  { key: 'damage_detail', title: 'Schadendetails', minCount: 2 },
  { key: 'closeup', title: 'Nahaufnahme', minCount: 2 }
];

const MobileUploadPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState({});
  const [progress, setProgress] = useState({ required_count: 0, required_total: 8 });
  const [currentSlot, setCurrentSlot] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    if (!caseId) {
      alert('Keine Fall-ID gefunden');
      return;
    }
    loadUploads();
  }, [caseId]);

  const loadUploads = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/case/${caseId}`);
      const data = await response.json();
      setUploads(data.slots || {});
      setProgress({
        required_count: data.required_count || 0,
        required_total: 8
      });
    } catch (error) {
      console.error('Load failed:', error);
    }
  };

  const handleUpload = async (slotKey, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('case_id', caseId);
      formData.append('slot_key', slotKey);
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload fehlgeschlagen');

      await loadUploads();
      
      // Auto-advance to next slot if current is complete
      const currentSlotData = PFLICHT_SLOTS[currentSlot];
      const currentImages = uploads[currentSlotData.key] || [];
      if (currentImages.length + 1 >= currentSlotData.minCount && currentSlot < PFLICHT_SLOTS.length - 1) {
        setTimeout(() => setCurrentSlot(currentSlot + 1), 500);
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setUploading(false);
    }
  };

  const currentSlotData = PFLICHT_SLOTS[currentSlot];
  const currentImages = uploads[currentSlotData?.key] || [];
  const isSlotComplete = currentImages.length >= (currentSlotData?.minCount || 1);

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-brand-green mb-4">
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Bilder hochladen</h1>
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Fortschritt:</span>
            <span className={`text-lg font-bold ${progress.required_count === 8 ? 'text-brand-green' : 'text-gray-900'}`}>
              {progress.required_count} / {progress.required_total}
            </span>
          </div>
        </div>
      </div>

      {/* Current Slot */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-4">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">{currentSlotData?.title}</h2>
              <p className="text-sm text-gray-600">
                {currentSlotData?.minCount > 1 ? `Mindestens ${currentSlotData.minCount} Bilder` : '1 Bild erforderlich'}
              </p>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isSlotComplete ? 'bg-brand-green/10 text-brand-green' : 'bg-yellow-50 text-yellow-700'
            }`}>
              {isSlotComplete ? 'Erledigt' : 'Fehlt'}
            </div>
          </div>

          {/* Current Images */}
          {currentImages.length > 0 && (
            <div className="grid grid-cols-2 gap-3 mb-4">
              {currentImages.map((img, idx) => (
                <div key={img.image_id} className="relative">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                    alt={currentSlotData.title}
                    className="w-full h-40 object-cover rounded-lg border border-gray-200"
                  />
                  <div className="absolute top-2 right-2 w-8 h-8 bg-brand-green/90 text-white rounded-lg flex items-center justify-center">
                    <Check className="w-5 h-5" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upload Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-6 text-lg rounded-xl font-semibold"
            >
              <Camera className="w-6 h-6 mr-3" />
              {uploading ? 'Wird hochgeladen...' : 'Foto aufnehmen'}
            </Button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(currentSlotData.key, file);
              }}
              className="hidden"
            />
          </div>
        </div>

        {/* Slot Navigation */}
        <div className="grid grid-cols-4 gap-2">
          {PFLICHT_SLOTS.map((slot, idx) => {
            const slotImages = uploads[slot.key] || [];
            const complete = slotImages.length >= slot.minCount;
            
            return (
              <button
                key={slot.key}
                onClick={() => setCurrentSlot(idx)}
                className={`py-3 rounded-xl text-xs font-semibold transition-all ${
                  idx === currentSlot ? 'bg-brand-green text-white' :
                  complete ? 'bg-brand-green/10 text-brand-green' :
                  'bg-white border border-gray-200 text-gray-600'
                }`}
              >
                {idx + 1}. {slot.title.split(' ')[0]}
                {complete && <Check className="w-3 h-3 inline ml-1" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MobileUploadPage;
